'use client'

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query'
import { fetchApi } from '@/lib/api/client'
import { SESSION_KEYS } from '@/lib/api/config'
import {
  can as canWith,
  canAny as canAnyWith,
  type PermissionAction,
  type PermissionRequirement,
  type PermissionSubject,
} from '@/lib/auth/permissions'
import { useHydrated } from '@/hooks/use-hydrated'
import { useAuthStore, useWorkspaceStore } from '@/stores'

/**
 * The signed-in person's grants in the CURRENT workspace —
 * `GET /v1/auth/me/permissions` (the workspace is the `x-workspace-id` header
 * `fetchApi` attaches).
 *
 * ── Loading and error FAIL OPEN ─────────────────────────────────────────────
 * Until the answer arrives (and when the call fails, or before hydration) the
 * screen renders exactly as it did before roles were enforced in the web app:
 * every menu entry shown, every button enabled. Hiding while loading would
 * flash entries away and back on every page load and workspace switch; hiding
 * on an error would turn one failed call into an empty sidebar. The API is the
 * security boundary and still refuses what the role cannot do.
 *
 * Only a KNOWN answer hides or disables: `status === 'ready'`.
 *
 * One deliberate exception, for PAGES rather than controls: the route gate
 * (components/auth/route-gate.tsx) holds back a page that has a rule while the
 * grants load, so the page's own requests never leave before the answer and
 * meet 403s. Menus and buttons still fail open as above.
 *
 * ── One answer, in the ROOT client ──────────────────────────────────────────
 * Announcements, Gamification, JIT and Smishing mount their own QueryProvider.
 * Reading the nearest client made the grants unknown again on every entry to
 * those modules — a flash of every menu entry and button, and a second
 * request. `PermissionsQueryRoot` (app/layout.tsx, inside the root
 * QueryProvider) hands the root client to every caller instead.
 *
 * ── The key: no workspace in it (apps/web/CLAUDE.md §1b) ────────────────────
 * `['auth', 'me', 'permissions', userId]`. Tenant isolation is
 * WorkspaceCacheGuard's (it clears the root client on a workspace or company
 * switch) plus the `x-workspace-id` header `fetchApi` attaches, as for every
 * module. The user stays in the key: §1b bans mode and workspace, and the
 * answer names no user, so a person change must never read the last person's.
 *
 * Two cases the guard does NOT cover, closed here rather than by a key:
 *   - the guard evicts in an EFFECT, so the first render after a switch still
 *     reads the previous workspace's cached answer (proved by the spec: one
 *     render under ws-2 with ws-1's grants, before this check existed);
 *   - the guard skips the first workspace the store settles on, which may
 *     differ from the one the session started with.
 * The answer names its workspace, so an answer for another one is treated as
 * unknown (`answerFitsWorkspace`), and re-asked when nothing evicts it.
 * Spec: hooks/__tests__/use-permissions.spec.tsx ("a workspace switch never
 * serves the previous workspace's grants").
 */

export interface MyPermissions {
  workspaceId: string | null
  role: string | null
  workspaceRole: string | null
  customRole: { id: string; name: string; artworkKey: string | null } | null
  permissions: string[]
}

export type PermissionsStatus = 'loading' | 'error' | 'ready'

export interface PermissionsState {
  status: PermissionsStatus
  /** The answer, when `status === 'ready'`. */
  data: MyPermissions | null
  /**
   * `true`/`false` once known; `null` while loading or after an error.
   * Callers decide what unknown means — the hooks below treat it as allowed.
   */
  can: (action: PermissionAction, subject: PermissionSubject) => boolean | null
  /** Same, for "any one of these" (a page that answers to several subjects). */
  canAny: (requirements: readonly PermissionRequirement[]) => boolean | null
}

export const permissionsQueryKeys = {
  all: ['auth', 'me', 'permissions'] as const,
  forUser: (userId: string | null) => [...permissionsQueryKeys.all, userId ?? 'anonymous'] as const,
}

/** workspace-store's company-wide view: not a workspace, and never persisted as one. */
const ALL_WORKSPACES_VIEW = 'all'

/**
 * Is an answer about the workspace this page is in? The API names the
 * workspace it resolved from `x-workspace-id` (auth.controller.ts
 * `myPermissions`), so the answer can be checked rather than trusted to the
 * cache key.
 *
 *   - no workspace in the answer: the bootstrap grants, about no page — no;
 *   - the company-wide view ('all') and the moment before the store has a
 *     workspace: requests carry the last real workspace (`getWorkspaceId()`),
 *     which is the one the answer names — yes;
 *   - otherwise the answer must name the active workspace.
 */
export function answerFitsWorkspace(answered: string | null, active: string | null): boolean {
  if (!answered) return false
  if (!active || active === ALL_WORKSPACES_VIEW) return true
  return answered.toLowerCase() === active.toLowerCase()
}

/**
 * Normalise the body to `MyPermissions`, or null when it carries no
 * `permissions` array. A body without the list is not a KNOWN "no": a spec
 * or proxy that answers every unknown request with `{}` would otherwise
 * read as "nothing granted" and disable every gated control. The hook turns
 * null into an error, i.e. fail-open.
 */
export function normalizeMyPermissions(raw: unknown): MyPermissions | null {
  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const inner = (body.data && typeof body.data === 'object' ? body.data : body) as Record<string, unknown>
  const str = (v: unknown) => (typeof v === 'string' ? v : null)
  if (!Array.isArray(inner.permissions)) return null
  const custom = inner.customRole && typeof inner.customRole === 'object'
    ? (inner.customRole as Record<string, unknown>)
    : null
  return {
    workspaceId: str(inner.workspaceId),
    role: str(inner.role),
    workspaceRole: str(inner.workspaceRole),
    customRole:
      custom && typeof custom.id === 'string'
        ? { id: custom.id, name: str(custom.name) ?? '', artworkKey: str(custom.artworkKey) }
        : null,
    permissions: inner.permissions.filter((p): p is string => typeof p === 'string'),
  }
}

/**
 * Will the grants be asked for at all? Without a session token `usePermissions`
 * never fetches, so its `loading` would last forever. The route gate reads this
 * so a page it would hold back while the grants load is not held forever
 * (components/auth/route-gate.tsx).
 */
export function hasAdminToken(): boolean {
  // Same rule as useEnabledModules: the shell also mounts on surfaces with no
  // admin session (the player, public pages), and an unauthenticated call
  // there is a 401 that signs the visitor out.
  if (typeof window === 'undefined') return false
  try {
    return !!window.localStorage.getItem(SESSION_KEYS.REAL_TOKEN_KEY)
  } catch {
    return false
  }
}

/**
 * A component rendered outside any QueryClientProvider (a bare component
 * spec, an isolated preview) still renders: the gate then never fetches and
 * stays unknown, i.e. fail-open. Without this, wrapping a button in a gate
 * would make every such render throw "No QueryClient set".
 */
let detachedClient: QueryClient | null = null
function detachedQueryClient(): QueryClient | undefined {
  // A spec that mocks @tanstack/react-query with only the hooks it uses has no
  // QueryClient constructor at all.
  if (typeof QueryClient !== 'function') return undefined
  detachedClient ??= new QueryClient()
  return detachedClient
}

/**
 * Read when the module has no `QueryClientContext` — a spec that mocks
 * @tanstack/react-query with only `{ useQuery, useMutation, useQueryClient }`.
 * `useContext(undefined)` throws, which broke every such spec once its buttons
 * were gated (2026-10-05). No client here means the gate stays unknown and
 * never reads the mocked `useQuery`'s data as grants.
 */
const NO_QUERY_CLIENT = createContext<QueryClient | undefined>(undefined)

/**
 * Same for a mock without `useQuery` at all. Chosen once, when the module
 * loads, so the hook calls the same function on every render.
 */
/** What the hook reads when a mocked useQuery returns nothing: no answer yet. */
const NO_ANSWER_YET = { data: undefined, isError: false } as const

const useGrantsQuery: typeof useQuery =
  typeof useQuery === 'function'
    ? useQuery
    : ((() => ({ data: undefined, isError: false })) as unknown as typeof useQuery)

/**
 * The ROOT QueryClient, for the grants only. Announcements, Gamification, JIT
 * and Smishing mount their own QueryProvider (a fresh QueryClient) around their
 * sidebar and pages, so a hook reading the NEAREST client started unknown and
 * asked the API again on every entry to those modules: menus and buttons
 * flashed from "everything" to "what the role may do" each time. The grants
 * belong to the person and the workspace, not to a module, so every caller
 * reads the one answer in the root client.
 */
const RootQueryClientContext = createContext<QueryClient | undefined>(undefined)

/**
 * Hands the root QueryClient to every `usePermissions` below it, through any
 * nested QueryProvider. Mounted ONCE, directly inside the root QueryProvider
 * (app/layout.tsx). The outermost one wins, so a stray nested copy cannot
 * re-point the grants at a module's client.
 */
export function PermissionsQueryRoot({ children }: { children?: ReactNode }) {
  const outer = useContext(RootQueryClientContext)
  const client = useContext(QueryClientContext ?? NO_QUERY_CLIENT)
  return createElement(RootQueryClientContext.Provider, { value: outer ?? client }, children)
}

/**
 * Refresh the signed-in person's grants after something changed them (a role
 * edited or given). Targets the ROOT client — where usePermissions reads the
 * answer — so a call from under a module's own QueryProvider still reaches it.
 */
export function useInvalidatePermissions(): () => Promise<void> {
  const rootClient = useContext(RootQueryClientContext)
  const nearestClient = useContext(QueryClientContext ?? NO_QUERY_CLIENT)
  const client = rootClient ?? nearestClient
  return useCallback(
    () => client?.invalidateQueries({ queryKey: permissionsQueryKeys.all }) ?? Promise.resolve(),
    [client],
  )
}

export function usePermissions(): PermissionsState {
  const hydrated = useHydrated()
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const rootClient = useContext(RootQueryClientContext)
  const nearestClient = useContext(QueryClientContext ?? NO_QUERY_CLIENT)
  const providedClient = rootClient ?? nearestClient
  const enabled = hydrated && providedClient !== undefined && hasAdminToken()

  const rawQuery = useGrantsQuery({
    queryKey: permissionsQueryKeys.forUser(userId),
    queryFn: async () => {
      const grants = normalizeMyPermissions(await fetchApi<unknown>('/auth/me/permissions'))
      if (!grants) throw new Error('GET /auth/me/permissions answered without a permissions list')
      return grants
    },
    enabled,
    // A role edited elsewhere applies on the API at once; let the web catch
    // up within a minute, and when the operator comes back to the tab.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  }, providedClient ?? detachedQueryClient())
  // A spec that mocks useQuery may hand back nothing at all: no answer yet, so fail open.
  const query = rawQuery ?? NO_ANSWER_YET

  const answered = query.data?.workspaceId ?? null
  const fits = !!query.data && answerFitsWorkspace(answered, workspaceId)
  // An answer for NO workspace (workspaceId null — the bootstrap grants of a broken or
  // workspace-less state) says nothing about this page: unknown, fail open ('error').
  // An answer for ANOTHER workspace is the previous workspace's, still cached in the
  // render between a switch and WorkspaceCacheGuard's eviction: unknown until this
  // workspace's arrives ('loading').
  const status: PermissionsStatus = !enabled
    ? 'loading'
    : fits
      ? 'ready'
      : query.data && !answered
        ? 'error'
        : query.isError
          ? 'error'
          : 'loading'
  const data = status === 'ready' ? (query.data ?? null) : null
  const permissions = data?.permissions ?? null

  // The cache held another workspace's answer and nothing evicted it — a switch on a
  // client without WorkspaceCacheGuard, or the first workspace the store settles on
  // differing from the one the session started with (the guard skips that first
  // change). Ask for this workspace's; while the guard's own refetch is in flight
  // this joins it (cancelRefetch: false), so it never adds a request.
  const answeredElsewhere = enabled && !!answered && !fits
  const refetch = (query as { refetch?: (options?: { cancelRefetch?: boolean }) => Promise<unknown> }).refetch
  useEffect(() => {
    if (answeredElsewhere && typeof refetch === 'function') void refetch({ cancelRefetch: false })
  }, [answeredElsewhere, refetch])

  const can = useCallback(
    (action: PermissionAction, subject: PermissionSubject) =>
      permissions === null ? null : canWith(permissions, action, subject),
    [permissions],
  )
  const canAny = useCallback(
    (requirements: readonly PermissionRequirement[]) =>
      permissions === null ? null : canAnyWith(permissions, requirements),
    [permissions],
  )

  return useMemo(() => ({ status, data, can, canAny }), [status, data, can, canAny])
}

/**
 * May the signed-in person do `action` on `subject` here?
 *
 * `allowed` is FAIL-OPEN: true while `loading` (and after a failed fetch), so
 * a control renders as it always did until the answer is known. Use
 * `loading` to tell the two apart.
 */
export function useCan(
  action: PermissionAction,
  subject: PermissionSubject,
): { allowed: boolean; loading: boolean } {
  const { status, can } = usePermissions()
  const known = can(action, subject)
  return { allowed: known !== false, loading: status === 'loading' }
}
